// A Neo answer whose stream dropped, read back by its turn (TOM-712, ADR 0264).
//
// Every model turn's stream opens with `turn { turnRef }`. When the stream then
// ends without its `done` or `error` frame (the link dropped, a proxy closed
// the connection), the chat asks `GET /v1/neo/turns/:turnRef` (contract
// §12.3.1) where the turn stands: still `running` (ask again in about two
// seconds), `done` with the stored answer (shown in place of what arrived), or
// `failed` / `canceled` (what arrived stays, with the error). Nothing here
// fetches: the chat reads, this file decides.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

import { parseNeoAnswer, type NeoAnswerRecord } from '@neomaris/protocol';

import { parseNeoAnswerCards, type NeoAnswerCard } from './neoAnswerCards';
import { parseNeoCharts, type NeoChart } from './neoChart';

/** The read-back URL for a turn. */
export function neoTurnUrl(turnRef: string): string {
  return `/v1/neo/turns/${encodeURIComponent(turnRef)}`;
}

/** About two seconds between reads of a running turn (§12.3.1). */
export const NEO_TURN_POLL_MS = 2_000;

/**
 * How long the chat keeps asking about a turn still running before it gives
 * up and says the connection dropped. A turn is at most seven bounded model
 * calls; this keeps the owner from waiting on a spinner for minutes.
 */
export const NEO_TURN_RECOVERY_MS = 120_000;

/** What the chat does after one read of a dropped turn. */
export type NeoTurnRecovery =
  /** Still running: read again after NEO_TURN_POLL_MS. */
  | { kind: 'wait' }
  /** Done: show the stored answer in place of what arrived. */
  | {
      kind: 'answer';
      messageId: string;
      content: string;
      conversationId: string | null;
      charts: NeoChart[];
      cards: NeoAnswerCard[];
      answer: NeoAnswerRecord | null;
    }
  /**
   * It ended without an answer, or cannot be read: keep what arrived and say
   * so. A failed turn carries the server's own words for it, when it sent any.
   */
  | {
      kind: 'lost';
      reason: 'failed' | 'canceled' | 'not_found' | 'unreadable';
      message?: string;
    }
  /** The session is gone or the turn is not the owner's to read: sign in again. */
  | { kind: 'auth'; status: 401 | 403 };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** A read's outcome: its 200 body, or the status it failed with (undefined: no response). */
export type NeoTurnRead = { ok: true; body: unknown } | { ok: false; status: number | undefined };

/**
 * What one read of the turn `turnRef` says to do. A read that failed without a
 * response (the link is still down) waits, as a running turn does; the chat's
 * time limit ends the wait. A 404, or a body for another turn, is lost.
 */
export function neoTurnRecovery(
  read: NeoTurnRead,
  turnRef: string,
  boatId?: string
): NeoTurnRecovery {
  if (!read.ok) {
    if (read.status === 401 || read.status === 403) return { kind: 'auth', status: read.status };
    if (read.status === undefined || read.status === 429 || read.status >= 500) {
      return { kind: 'wait' };
    }
    return { kind: 'lost', reason: read.status === 404 ? 'not_found' : 'unreadable' };
  }
  const body = read.body;
  if (!isObject(body) || body.turnRef !== turnRef) return { kind: 'lost', reason: 'unreadable' };
  switch (body.status) {
    case 'running':
      return { kind: 'wait' };
    case 'failed': {
      const error = isObject(body.error) ? body.error : null;
      const message =
        typeof error?.message === 'string' && error.message.trim().length > 0
          ? error.message.trim()
          : undefined;
      return { kind: 'lost', reason: 'failed', ...(message === undefined ? {} : { message }) };
    }
    case 'canceled':
      return { kind: 'lost', reason: 'canceled' };
    case 'done':
      break;
    default:
      return { kind: 'lost', reason: 'unreadable' };
  }
  const message = body.message;
  const messageId = typeof body.messageId === 'string' ? body.messageId : turnRef;
  if (!isObject(message) || typeof message.content !== 'string') {
    return { kind: 'lost', reason: 'unreadable' };
  }
  // On a boat page a chart or card of another boat is dropped, as the stream's are.
  return {
    kind: 'answer',
    messageId,
    content: message.content,
    conversationId: typeof body.conversationId === 'string' ? body.conversationId : null,
    charts: parseNeoCharts(body.charts, boatId),
    cards: parseNeoAnswerCards(body.cards, boatId),
    // Null when the turn stored no record (asked without `answers`, or before
    // TOM-696), or one this client cannot read: the answer shows without it.
    answer: parseNeoAnswer(body.answer),
  };
}
